import { deriveBmi, followUpDate, heightLabel, heightText, shortDate } from "./rxMappers";

export { followUpDate };

/**
 * Review-prescription sample data + the derivation that turns the builder's
 * selections into the document preview. Source: Figma "Screen / review
 * prescription" (1762:15386) — left builder Cards (1762:16353), right
 * Prescription document (1762:16579). UI-only; replace with the Rx API later.
 *
 * The right pane is a LIVE preview: every line in the document is derived from
 * what is ticked on the left, so the two panes never drift. `buildDocument()`
 * is that single derivation.
 */

/* ---------- prescriber (Doc header + signature) ----------
   BLANK, not a sample doctor. The real prescriber is the logged-in doctor and
   arrives via buildDocument's `prescriber` arg; this is only what the document
   shows in the instant before that resolves. A named sample here printed an
   invented doctor + licence number on a real patient's prescription — and it
   is a signature block, so it must never show a name that isn't the signer. */
export const PRESCRIBER = {
  name: "",
  qualification: "",
  license: "",
};

export const DOC_FOOTER =
  "The scope of this document is limited to the information provided by the patient to the Zyla team over the phone or Zyla app and should not be used for medico legal purposes.";

/* Blank for the same reason: the live value is the day the prescription is
   written, passed in by the screen. A fixed date would print a wrong one. */
export const CONSULTATION_DATE = "";

/* ---------- editable patient details (Card 2215:3197) ---------- */
export const PATIENT_FIELDS = {
  // Empty, not sample: this screen is API-backed, and a sample weight/phone
  // would win over the real value in every `prev.x || fetched` prefill.
  fullName: "",
  age: "",
  gender: "",
  weight: "",
  phone: "",
  // Height/weight-date/BMI: real values arrive from the patient's vitals; BMI is
  // always derived from the other two, never typed. dateOfWeightRecorded is
  // REQUIRED by the prescription API.
  height: "",
  // Empty like the rest, and for the same reason: the rpd prefill is a
  // `prev.heightUom || fetched` merge, so a "REFLD250" default here won the
  // race every time and the patient's real unit never landed. deriveBmi treats
  // anything that isn't the ft/in code as cm, so "" is a safe display default.
  heightUom: "",
  dateOfWeightRecorded: "",
};

export const GENDERS = ["Female", "Male", "Other"];

/**
 * The five picker cards. Every row: { id, label, isNew?, isUpdated?, icd?, sub? } plus the
 * per-card extras the document needs. `group` is the caption above the rows —
 * Figma spells it "ADDED  IN THIS CONSULATION" (sic) on the cards.
 */
export const PICKERS = [
  /* Complaints first — see SECTION_TABS in ReviewPrescription. This is the
     pre-fetch fallback and must match the live list in done(), or the cards
     would visibly reorder the moment the records land. */
  { key: "complaints", title: "Chief complaints", groups: [] },
  { key: "diagnosis", title: "Diagnosis", groups: [] },
  { key: "reports", title: "Lab investigations", groups: [] },
  { key: "medicines", title: "Medicine", groups: [] },
  /* No investigations entry: "Advised investigations" is not a picker — it has
     its own card (AdvisedInvestigationsCard) that CREATES rows from the lab-test
     catalogue rather than choosing from the patient's record. */
];

/**
 * Nothing ticked on load. The real selection is derived from the fetched rows
 * — everything this consultation added or updated (`isNew` / `isUpdated`) — in
 * ReviewPrescription's `done()`. These ids pointed at the sample deck's rows, so they could only
 * ever tick invented data.
 */
export const INITIAL_PICKS = {
  diagnosis: [],
  complaints: [],
  reports: [],
  medicines: [],
};

/* Card · Custom instruction (2113:3249) used to open pre-filled with a sample
   pointer. Removed, not kept unused: on a live screen it put condition-specific
   advice one click away from any patient's prescription. The field starts empty
   with a placeholder instead. */

/**
 * Card · Referrals (2398:3210) — EMPTY.
 * This used to seed a full sample referral (a cardiology consult citing an
 * invented LDL of 190 mg/dL). Only a saved draft overwrites it, so on every
 * fresh prescription that fabricated referral rendered into the document and
 * would have been published with it.
 */
export const INITIAL_REFERRALS = [];

/* Blank — the specialty and reason are the doctor's to write. Prefilling a
   clinical reason put words in their mouth one click from a signed document. */
/* No `doctor` field: the card had a "Preferred doctor / centre" input that nothing
   consumed — neither buildDocument nor buildSaveBody ever read it — so it collected
   text and discarded it. Removed rather than wired up, since the prescription has no
   place to print it. */
export const REFERRAL_FORM = {
  specialty: "",
  reason: "",
  within: "1 week",
};
/* The fixed windows. "Pick date" is NOT one of them — it used to be a fourth entry
   here, which made it a plain pill that simply set the string "Pick date", so the
   prescription printed "within Pick date of this prescription". It is now a control
   that reveals a date field (see ReferralsCard). */
export const WITHIN_OPTIONS = ["3 days", "1 week", "2 weeks"];

/* A picked date is stored as a complete phrase — "by 5 Sep, 2026" — rather than a
   bare date, because the sentence around it differs: a window reads "within 1 week
   of this prescription", a deadline reads "by 5 Sep, 2026". `withinPhrase` is the
   ONE place that difference is expressed, shared by the card, the document preview
   and (mirrored) api-prescription-service's transformer. */
export const BY_PREFIX = "by ";
export const isByDate = (within) =>
  String(within || "").toLowerCase().indexOf(BY_PREFIX) === 0;
export const withinPhrase = (within) => {
  const v = String(within || "").trim();
  if (!v) return "";
  return isByDate(v) ? v : `within ${v} of this prescription`;
};

/** Card · Follow-up. Variant "date" (2225:3083) and "interval" (2402:3211). */
/* Follow-up is INTERVAL ONLY (Nittin, 2026-07-27) — the "date" variant and its
   time-slot picker are gone; the date is derived from the prescription date. */
/* `scheduled` is what puts the follow-up line INTO the prescription (Nittin,
   2026-07-27). The interval is just a setting until "Schedule follow-up" is
   clicked; Cancel takes it back out. It starts false, so a prescription never
   carries a follow-up nobody asked for. */
export const INITIAL_FOLLOW_UP = {
  count: 2,
  unit: "Weeks",
  scheduled: false,
};

/* Auto-calc note on variant B — mirrors Figma's "2 weeks → 28 Jul 2026". */
/* followUpDate now lives in rxMappers and is re-exported here so existing importers
   keep working. It moved because the save body needs the SAME date the document
   prints: the follow-up is stored twice — as the instruction sentence and as a
   structured field — and two copies of the date maths would eventually disagree. */

const NOTE_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const FULL_MONTHS = ["January","February","March","April","May","June","July",
  "August","September","October","November","December"];
export const formatFollowUpDate = (d) =>
  d ? `${d.getDate()} ${NOTE_MONTHS[d.getMonth()]} ${d.getFullYear()}` : "";
/** "28 July 2026" — the document's form (rendered uppercase by .rxdoc-instr-item). */
export const formatFollowUpDateLong = (d) =>
  d ? `${d.getDate()} ${FULL_MONTHS[d.getMonth()]} ${d.getFullYear()}` : "";

/**
 * "Days" -> "Day/s". The one place the follow-up unit's WORDING lives.
 *
 * Product wanted the "/s" form everywhere it appears in Create Rx (2026-09-10),
 * which is three places: the interval buttons, the card's own note, and the
 * sentence pushed into the prescription's Other Instructions. That third one is
 * also what the generated PDF prints — the PDF renders the `instructions[]`
 * this file builds, so it needs no backend change to follow.
 *
 * THE STORED VALUE IS UNTOUCHED. `followUp.unit` stays "Days"/"Weeks": it goes
 * on the wire (rxMappers followUpBody), it is parked in the builder's
 * scratchpad, and `followUpDays` keys the date arithmetic off it. Only the
 * wording moves, so this maps rather than renames.
 *
 * Unknown units pass through unchanged rather than being forced into a shape
 * they do not have — the card offers exactly two, but a scratchpad parked by an
 * older build could hold something else.
 */
const UNIT_LABELS = { days: "Day/s", weeks: "Week/s" };
export const unitLabel = (unit) => {
  const key = String(unit == null ? "" : unit).trim().toLowerCase();
  return UNIT_LABELS[key] || String(unit == null ? "" : unit);
};

export const intervalNote = (count, unit) => {
  const when = formatFollowUpDate(followUpDate(count, unit));
  return `Date auto-calculates from the prescription date — ${count} ${unitLabel(unit).toLowerCase()}${
    when ? ` → ${when}` : ""
  }.`;
};

/* ---------- the derivation: builder selections → document ---------- */

/**
 * Flatten a picker's groups to rows. `pickers` is the LIVE card list when the
 * screen has fetched real data; it falls back to the sample deck so the
 * component still renders before the first response lands.
 */
const rowsOf = (key, pickers) =>
  ((pickers || PICKERS).find((p) => p.key === key) || { groups: [] }).groups.reduce(
    (all, g) => all.concat(g.rows),
    []
  );

/** Rows of `key` that are currently ticked, in card order. */
export const pickedRows = (key, picks, pickers) =>
  rowsOf(key, pickers).filter((r) => (picks[key] || []).indexOf(r.id) !== -1);

/**
 * Everything the document renders, derived from builder state. Keeping this in
 * one place is what stops the preview drifting from the selections.
 */
export const buildDocument = ({
  patient,
  picks,
  customInstructions,
  referrals,
  followUp,
  pickers,
  prescriber,
  consultationDate,
  // Advised investigations aren't picked from the record — the card creates
  // them — so they arrive as rows rather than through `picks`.
  investigations: investigationRows,
}) => {
  /* NO ICD CODE ON EITHER LINE (Nittin, 2026-09-24). The two documents had it
     crossed: the wire put the code in front of the CHIEF COMPLAINT, so the
     generated PDF read "J02.9 Sore throat" where this pane said "Sore throat",
     while this pane put it in front of the DIAGNOSIS, where the PDF never had
     it. A code is for the record and for the coder — the patient's copy names
     the condition. It is not lost: the picker row still shows it while the
     doctor chooses, and the payload carries it in each row's `value`. */
  const diagnosis = pickedRows("diagnosis", picks, pickers).map((r) => r.label);
  const complaints = pickedRows("complaints", picks, pickers).map((r) => r.label);
  const reports = pickedRows("reports", picks, pickers).map((r) => r.docLabel || r.label);
  /* Board 3031:9853 draws FOUR columns: MEDICINE / FREQUENCY (DOSE QTY.) /
     INSTRUCTIONS / TIME PERIOD, with the dosage form as a grey suffix after the
     name rather than a prefix on it, and the dose folded into the frequency
     rather than standing in a column of its own. The `doc*` fields carry those
     shapes (rxMappers); `label`/`frequency`/`duration` are the PDF/card forms
     and stay as the fallback for any row without them. */
  const medicines = pickedRows("medicines", picks, pickers).map((r) => ({
    name: r.docName || r.label,
    form: r.docForm || "",
    frequency: r.docFrequency || r.frequency || "",
    instructions: r.instructions || "",
    duration: r.docDuration || r.duration || "",
    // The date span the duration covers, printed under it. "" when the row has
    // no start date or runs indefinitely — the board shows no second line then.
    dates: r.docDates || "",
  }));
  // "TEST NAME - instruction", byte for byte what the PDF prints
  // (api-prescription-service TransformMetaPrescriptionPdf:
  //  strings.ToUpper(label) + " - " + instruction). The preview said just the
  // name before, which would have understated what the patient receives.
  const investigations = (investigationRows || []).map((r) => {
    const name = String((r.labTest && r.labTest.label) || "").toUpperCase();
    return r.instruction ? `${name} - ${r.instruction}` : name;
  });

  // Other Instructions = standing advice, then each custom pointer, then the
  // follow-up line last (Figma numbers them 1..5 in exactly that order).
  const otherInstructions = (customInstructions || []).slice();
  // Only once it has actually been scheduled — NOT merely configured. The old
  // `followUp.count` test was always true (the stepper clamps at 1), so every
  // prescription carried a follow-up line whether or not one was intended.
  if (followUp.scheduled) {
    // Rendered uppercase by the document, e.g.
    // "FOLLOW-UP IS SUGGESTED AFTER 2 WEEKS (28 JULY 2026)".
    //
    // Counted from `consultationDate` — the SAME base buildSaveBody uses for the
    // stored `followUp.date`. Both used to call `new Date()` independently, so a
    // save that straddled midnight printed one day and stored another.
    const on = formatFollowUpDateLong(
      followUpDate(followUp.count, followUp.unit, consultationDate)
    );
    otherInstructions.push(
      `Follow-up is suggested after ${followUp.count} ${unitLabel(followUp.unit).toLowerCase()}${
        on ? ` (${on})` : ""
      }`
    );
  }

  return {
    // Real prescriber / date once fetched; the sample constants are only the
    // pre-load fallback.
    prescriber: prescriber || PRESCRIBER,
    // Formatted HERE, from the same Date the save body carries. The caller used to
    // pass a pre-formatted "17 Aug, 2026" string while buildSaveBody made its own
    // `new Date()` — two clocks for one fact, and the follow-up line counted off a
    // display string that only parsed by luck of the lenient Date parser.
    consultationDate: consultationDate ? shortDate(consultationDate) : CONSULTATION_DATE,
    patient: {
      name: patient.fullName,
      /* "6 mo/F" under a year, "36/F" above it — the same rule the card and
         the PDF follow. Without the months an infant printed as "/F". */
      ageGender: `${patient.ageMonths ? `${patient.ageMonths} mo` : patient.age}/${(patient.gender || "").charAt(0) || "-"}`,
      height: heightText(patient.height, patient.heightUom) || "-",
      /* The unit belongs over the number, not beside it — "Height (cm)", like
         the "Weight (Kg)" next to it (Nittin, 2026-09-24). */
      heightLabel: heightLabel(patient.heightUom),
      weight: patient.weight || "-",
      bmi: deriveBmi(patient) || "-",
    },
    diagnosis,
    complaints,
    reports,
    medicines,
    otherInstructions,
    investigations,
    referrals,
  };
};
